// EDEN · the shared navigation model — the SINGLE source of truth for the desktop sidebar
// (src/chrome.ts) and the phone tab bar + its More sheet (src/mobile-nav.ts), so they can never
// drift. Every `href` points at an EXISTING route (a data-link target the router's global click
// listener already handles). Coaching feedback stays a 1-on-1 surface; the wall stays structural:
// no coaching content ever leaks onto a team surface.
//
// IA (2026-09-30 redesign, owner-approved): five places a teacher goes, two more for the Lead.
//   Home · Year plan · My plans · Worksheets & tests · Calendar   (+ Lead: Team · Setup)
// Home carries this week's lesson for every class, so the old This Week tab folds into it (the
// /this-week route still works and counts as Home). Secondary pages (Settings, My observations,
// the team board, My classes) live in the account area and the phone's More sheet.

import type { StringKey } from "./i18n";
import type { Me } from "./state";

/** The speaking coach is set aside (owner, 2026-08-04). Its entry points are hidden, never deleted:
 *  the Coach leaf below (the sidebar, the phone tab bar and its More sheet all read it through
 *  sectionsFor / mobileTabs), the "Talk it through" road on /plan/start (pages/plan-fork.ts) and the
 *  AI-keys section on Settings (pages/settings.ts). Its routes (/coach, /speak/*) and their code
 *  stay. Set this to false to bring every entry point back. */
export const COACH_SET_ASIDE = true;

export interface NavLeaf {
  href: string; // an existing route
  key: StringKey; // label
  tabKey?: StringKey; // shorter label for the phone tab bar (falls back to `key`)
  subKey?: StringKey; // one-line description (the phone's More sheet)
  icon: string; // an icon name from src/icons.ts
  primary?: boolean; // the elevated "Plan a lesson" button — rendered filled + first, never a plain row
  tab?: "teacher" | "lead" | "both"; // which phone tab bars carry it (the rest go into More)
  role?: "lead"; // leaf visible only to this role
  setAside?: boolean; // hidden from every nav surface while its feature is set aside (kept, not deleted)
  also?: string[]; // other paths that count as "here" for this item (e.g. /this-week under Home)
  leadKey?: StringKey; // the Lead's label, where the Lead's page is wider than a teacher's (/classes)
  leadSubKey?: StringKey;
}

export interface NavSection {
  key: StringKey; // section header
  role?: "lead"; // lead-only section (omitted for teachers)
  secondary?: boolean; // shown in the sidebar footer / More sheet, never as a main row
  items: NavLeaf[];
}

export const NAV: NavSection[] = [
  {
    key: "nav.teaching",
    items: [
      { href: "/plan/start", key: "week.plan_button", icon: "plus", primary: true },
      { href: "/", key: "nav.home", icon: "home", tab: "both", also: ["/this-week"] },
      { href: "/roadmap", key: "nav.year_plan", tabKey: "nav.tab_year_plan", subKey: "nav.year_plan_sub", icon: "map", tab: "both", also: ["/map"] },
      { href: "/books", key: "nav.books", subKey: "nav.books_sub", icon: "book", tab: "both" },
      { href: "/pdf-books", key: "nav.pdf_books", subKey: "nav.pdf_books_sub", icon: "pdf" },
      { href: "/plans", key: "nav.my_plans", subKey: "nav.my_plans_sub", icon: "plans", tab: "teacher", also: ["/plan", "/lesson"] },
      { href: "/tests", key: "nav.tests", subKey: "nav.tests_sub", icon: "tests" },
      { href: "/calendar", key: "nav.calendar", subKey: "nav.calendar_sub", icon: "calendar" },
      { href: "/coach", key: "home.coach", tabKey: "nav.tab_coach", subKey: "home.coach_sub", icon: "obs", setAside: COACH_SET_ASIDE },
    ],
  },
  {
    key: "nav.leadership",
    role: "lead",
    items: [
      { href: "/team", key: "nav.team", subKey: "nav.team_sub", icon: "team", tab: "lead", role: "lead", also: ["/status", "/insights"] },
      { href: "/admin", key: "nav.setup", subKey: "nav.setup_sub", icon: "setup", role: "lead" },
    ],
  },
  {
    key: "nav.account",
    secondary: true,
    items: [
      { href: "/classes", key: "nav.my_classes", subKey: "nav.my_classes_sub", icon: "classes", leadKey: "nav.classes", leadSubKey: "nav.classes_sub" },
      { href: "/observations", key: "home.observations", subKey: "nav.observations_sub", icon: "obs", also: ["/observation"] },
      { href: "/dashboard", key: "nav.board", subKey: "nav.board_sub", icon: "board" },
      { href: "/settings", key: "common.settings", subKey: "nav.account_sub", icon: "user" },
    ],
  },
];

/** A leaf any nav surface may show: not set aside. */
function shown(leaf: NavLeaf): boolean {
  return !leaf.setAside;
}

/** The sections a given role should see (teachers never see the Lead group), each without its
 *  set-aside leaves. */
export function sectionsFor(role: Me["auth_role"]): NavSection[] {
  return NAV.filter((s) => !s.role || s.role === role).map((s) => ({
    ...s,
    items: s.items
      .filter((i) => shown(i) && (!i.role || i.role === role))
      .map((i) => (role === "lead" && i.leadKey ? { ...i, key: i.leadKey, subKey: i.leadSubKey ?? i.subKey } : i)),
  }));
}

/** Active when the current path IS the item, sits under it (e.g. /classes/123 → My classes), or is
 *  one of the item's `also` paths (or under one). Home ("/") is only ever an exact match itself. */
export function isActive(path: string, href: string): boolean {
  // The plan form belongs to "Plan a lesson": /plan/start is its doorway and /plan/new the form itself. The form used to
  // light "My plans" (its `also` path /plan), so a teacher in it believed they were in the list (owner, 21 Oct 2026).
  if (path === "/plan/new" || path === "/plan/start") return href === "/plan/start";
  const leaf = NAV.flatMap((s) => s.items).find((i) => i.href === href);
  const under = (p: string, base: string): boolean =>
    base === "/" ? p === "/" : p === base || p.startsWith(base + "/");
  if (under(path, href)) return true;
  // /plan/start is the CTA's own route: it must not also light "My plans" (/plan), which owns
  // /plan/:id and /plan/new — so an `also` match never claims a path another leaf owns exactly.
  if (leaf?.also?.some((a) => under(path, a))) {
    return !NAV.flatMap((s) => s.items).some((i) => i.href !== href && i.href === path);
  }
  return false;
}

/** The single elevated CTA (Plan a lesson), rendered filled at the top of the sidebar. */
export function primaryLeaf(): NavLeaf | undefined {
  return NAV.flatMap((s) => s.items).find((i) => i.primary && shown(i));
}

/** The main (non-secondary, non-CTA) leaves for a role, in order. */
export function mainLeaves(role: Me["auth_role"]): NavLeaf[] {
  return sectionsFor(role)
    .filter((s) => !s.secondary)
    .flatMap((s) => s.items)
    .filter((i) => !i.primary);
}

/** The leaves that belong in the phone's bottom tab bar for a role (a "More" tab is added
 *  synthetically). Teacher: Home · Year plan · Books · My plans. Lead: Home · Team · Year plan · Books. */
export function mobileTabs(role: Me["auth_role"]): NavLeaf[] {
  const all = sectionsFor(role).flatMap((s) => s.items);
  // T1 review (owner, 9 Oct 2026): Books is a tab of its own — teachers start from the book.
  const want = role === "lead" ? ["/", "/team", "/roadmap", "/books"] : ["/", "/roadmap", "/books", "/plans"];
  return want.map((h) => all.find((i) => i.href === h)).filter((i): i is NavLeaf => !!i && (i.tab === "both" || i.tab === role));
}
